import { useEffect, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { http, formatApiError } from "../lib/api";

const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const label = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const AckBar = ({ month, receipt, onAck }) => {
  const [busy, setBusy] = useState(false);
  if (receipt?.status === "confirmed")
    return <div data-testid="agent-planning-acked" className="mt-3 flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3"><CheckCircle2 className="h-4 w-4" /> Planning confirmé le {fmt(receipt.acked_at)}</div>;
  const ack = async () => { setBusy(true); try { onAck((await http.post("/agent-app/planning/ack", { month })).data); } catch (e) { alert(formatApiError(e)); } finally { setBusy(false); } };
  return (
    <div className="mt-3">
      {receipt?.acked_at && <div data-testid="agent-planning-changed" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-2">Votre planning a été modifié depuis votre dernière confirmation.</div>}
      <button data-testid="agent-planning-ack" onClick={ack} disabled={busy} className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold">
        {busy ? "Enregistrement…" : "J'ai pris connaissance de mon planning"}
      </button>
    </div>
  );
};

export default function AgentPlanning() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" }));
  const [zoom, setZoom] = useState(false);
  const months = [ym(now), ym(new Date(now.getFullYear(), now.getMonth() + 1, 1))];
  const [month, setMonth] = useState(months[0]);
  const [pages, setPages] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    setPages(null); setErr(""); setReceipt(null);
    http.get("/agent-app/planning", { params: { month } }).then((r) => { if (alive) { setPages(r.data.pages); setReceipt(r.data.receipt); } }).catch((e) => { if (alive) setErr(formatApiError(e)); });
    return () => { alive = false; };
  }, [month]);
  return (
    <section data-testid="agent-planning">
      <h2 className="font-display text-lg font-extrabold mb-2">Mon planning</h2>
      <div className="flex gap-2 mb-3">
        {months.map((m) => (
          <button key={m} data-testid={`agent-planning-month-${m}`} onClick={() => setMonth(m)} className={`flex-1 py-2 rounded-lg text-sm font-bold capitalize border ${m === month ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{label(m)}</button>
        ))}
      </div>
      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{err}</div>}
      {!pages && !err && <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
      {pages && <button data-testid="agent-planning-zoom" onClick={() => setZoom(!zoom)} className="mb-2 text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-300 bg-white">{zoom ? "Vue d'ensemble" : "Agrandir (×2)"}</button>}
      <div className={`space-y-3 select-none ${zoom ? "overflow-x-auto" : ""}`} onContextMenu={(e) => e.preventDefault()}>
        {pages?.map((p, i) => (
          <img key={i} data-testid={`agent-planning-page-${i}`} src={`data:image/png;base64,${p}`} alt={`Planning page ${i + 1}`} draggable={false}
               className={`${zoom ? "w-[200%] max-w-none" : "w-full"} rounded-lg border border-slate-200 shadow-sm bg-white pointer-events-none`} />
        ))}
      </div>
      {pages && <AckBar month={month} receipt={receipt} onAck={(r) => setReceipt({ ...receipt, ...r })} />}
      <p className="text-[11px] text-slate-400 mt-2 text-center">Consultation uniquement · données à jour à chaque ouverture</p>
    </section>
  );
}
